/**
 * The typed admin API client.
 *
 * One method per admin endpoint, each returning the contract type for that route. It
 * owns three cross-cutting concerns so no page has to:
 *   1. attaches the `Authorization: Bearer <token>` header (token from `getToken`),
 *   2. maps every non-2xx envelope to a typed `ApiError` in one place, and
 *   3. on a 401 fires `onUnauthorized` (which drops the session and routes to login)
 *      before throwing, so an expired admin token can never leave a page half-usable.
 */
import type {
  AdminBanner,
  AdminBannersResponse,
  CreateBannerRequest,
  UpdateBannerRequest,
  AdminLoginRequest,
  AdminLoginResponse,
  AdminMe,
  AdminSession,
  ApiErrorCode,
  Flag,
  FlagsResponse,
  ProviderConfig,
  ProviderCapability,
  ProviderStatus,
  ProviderEgress,
  ProviderStatusResponse,
  SetFlagRequest,
  SetProviderConfigRequest,
  SetProviderCredentialsRequest,
  // Deposit rails — the minimum deposit and confirmation depth per (currency, network)
  AdminAssetRail,
  AdminAssetRailsResponse,
  SetAssetOverrideRequest,
  // Fees — the house spread, every basis-point fee, and the bill schedule
  AdminFee,
  AdminFeesResponse,
  SetFeeRequest,
  // Live rates + the operator override
  AdminRateRow,
  AdminRatesResponse,
  SetRateOverrideRequest,
  // Admin console read models — now published by @genex/contracts
  AdminKycDetail,
  AdminKycQueueResponse,
  AdminKycPendingResponse,
  KycRecheckResponse,
  // Operator-set tier ceilings
  AdminTierLimitsResponse,
  SetTierLimitsRequest,
  TierLimit,
  AdminTxKind,
  AdminTxStatus,
  AdminBank,
  AdminBanksResponse,
  BrandAsset,
  BrandHeroAsset,
  BrandHeroSurface,
  ReconciliationResponse,
  GrantTierRequest,
  ReviewKycRequest,
  SetHeroTextColorRequest,
  SuspendUserRequest,
  UploadBrandAssetRequest,
  SplashAsset,
  UploadSplashAssetRequest,
  // Gift cards
  AdminGiftcardBrand,
  AdminGiftcardBrandsResponse,
  AdminRedemptionDetail,
  AdminRedemptionQueueResponse,
  CreateGiftcardBrandRequest,
  ReviewRedemptionRequest,
  UpdateGiftcardBrandRequest,
  // Support console
  AdminInboxQuery,
  AdminThreadDetail,
  AdminThreadRow,
  AdminReplyRequest,
  AssignThreadRequest,
  SupportSettings,
  SetSupportSettingsRequest,
  // Business Settings / white-label
  PlatformSettings,
  SetPlatformSettingsRequest,
  // Legal & about documents
  AdminLegalDocument,
  AdminLegalDocumentsResponse,
  UpsertLegalDocumentRequest,
  // Email templates
  EmailTemplatesResponse,
  EmailTemplate,
  UpsertEmailTemplateRequest,
  RenderPreviewRequest,
  RenderPreviewResponse,
  TestSendRequest,
  // The email layout, the image library, and broadcasts
  EmailLayout,
  EmailLayoutHistoryResponse,
  UpdateEmailLayoutRequest,
  RestoreEmailLayoutRequest,
  EmailImage,
  EmailImagesResponse,
  UploadEmailImageRequest,
  EmailBroadcast,
  EmailBroadcastDetail,
  EmailBroadcastsResponse,
  CreateBroadcastRequest,
  BroadcastPreviewRequest,
  BroadcastPreviewResponse,
  SmtpTestRequest,
  SmtpTestResponse,
  // Push
  BroadcastPushRequest,
  // Operator surfaces from `admin-ops.ts`, all of which landed while this console was being
  // built: the account-state write, the manual balance movement, the KYC document metadata,
  // and the bill-service icon upload.
  AdminAdjustBalanceRequest,
  AdminAdjustBalanceResponse,
  AdminRefreshRequest,
  BillService,
  KycDocumentsResponse,
  SetAccountStateRequest,
  UploadBillIconRequest,
  // The read models that carried a `Pending…` shim until the contract published the fields
  // they were waiting for — the customer's photo, the full phone, the vendor cost, the masked
  // document number. See the retirement note in `contracts/pending.ts`.
  AdminUserDetail,
  AdminUsersResponse,
  AdminTxDetail,
  AdminTxResponse,
  AdminInboxResponse,
  AdminTreasuryOrder,
  TreasuryOverviewResponse,
  TreasuryThresholds,
  SetTreasuryThresholdsRequest,
  TreasuryRebalance,
  RecordRebalanceRequest,
  TreasuryReconciliationResponse,
  ReconciliationScope,
  ReviewOrderRequest,
  TreasuryReviewQueueResponse,
} from '../contracts/types.js';
import { ADMIN_ROUTES, API_PREFIX } from '../contracts/routes.js';
/*
 * The provisional half of the surface, and it is down to one thing.
 *
 * Everything else `pending.ts` once carried — the customer's photo and its route, the full
 * phone on a transaction and a thread, the account states, the KYC document bytes and
 * metadata, manual balance movements, the vendor cost — has landed in the contract and moved
 * to `contracts/types.js` and `contracts/routes.js`, which is the lifecycle that file exists
 * to have. What is left is the per-service bill write: the contract publishes the catalogue
 * and the icon upload but not the patch, so the letter mark and the colour behind it are still
 * seed-only, and they are the fallback every service without an uploaded icon is drawn from.
 */
import { PENDING_ROUTES } from '../contracts/pending.js';
import type {
  PendingBushaProbeRequest,
  PendingSetProviderServicesRequest,
  PendingUpdateBillServiceRequest,
} from '../contracts/pending.js';
import { ApiError } from './errors.js';
import type { HttpMethod, RawRequest, Transport } from './transport.js';

/** GET /admin/providers — every capability's routing config. */
export interface AdminProvidersResponse {
  readonly providers: readonly ProviderConfig[];
}

/**
 * One support attachment, fetched over the bearer-scoped media route as raw bytes and handed
 * to the UI as an object URL. The route streams binary (an image or a PDF), so it must not go
 * through the JSON path — `getMediaObject` reads it via the transport's `sendMedia`, wraps the
 * `Blob` in an object URL for an `<img src>` or a download link, and hands back `revoke` so the
 * caller frees it. This replaces the earlier base64 path that blanked images by `JSON.parse`-ing
 * their bytes.
 */
export interface MediaObject {
  readonly url: string;
  readonly mime: string;
  readonly name: string;
  /** Release the object URL when the attachment is no longer shown. */
  readonly revoke: () => void;
}

/** The mock/real ack for a template test send. */
export interface TestSendResult {
  readonly ok: boolean;
  readonly to?: string;
}

/** The result of a push broadcast — how many devices it will reach. */
export interface PushBroadcastResult {
  readonly recipients: number;
  readonly category?: string;
}

/** The operator inbox filters (mirrors the contract's `adminInboxQuerySchema`). */
export type SupportInboxFilters = Partial<Pick<AdminInboxQuery, 'status' | 'priority' | 'assignedAdminId' | 'cursor'>>;

/**
 * The server-supported transaction filters (see the contract's `adminTxFilterSchema`).
 * Free-text search over reference/phone is a client-side concern over the returned page —
 * the API filters on `kind`/`status`/`cursor`, never a text query.
 */
export interface TransactionFilters {
  readonly kind?: AdminTxKind;
  readonly status?: AdminTxStatus;
  /** A calendar day, `YYYY-MM-DD`. The server covers the whole of it, both ends inclusive. */
  readonly from?: string;
  readonly to?: string;
  readonly cursor?: string;
  /** 1–100, server default 50. The only lever on page size the contract offers. */
  readonly limit?: number;
}

interface ErrorEnvelope {
  error: { code: ApiErrorCode; message: string; requestId?: string; retryAfterSeconds?: number };
}

function isErrorEnvelope(v: unknown): v is ErrorEnvelope {
  return (
    typeof v === 'object' &&
    v !== null &&
    'error' in v &&
    typeof (v as { error: unknown }).error === 'object'
  );
}

export interface AdminApiOptions {
  readonly transport: Transport;
  readonly getToken: () => string | null;
  readonly onUnauthorized: () => void;
}

export class AdminApi {
  constructor(private readonly opts: AdminApiOptions) {}

  // ── core request ──
  private async request<T>(
    method: HttpMethod,
    path: string,
    extra?: Partial<Pick<RawRequest, 'body' | 'form' | 'query'>>,
  ): Promise<T> {
    const res = await this.opts.transport.send({
      method,
      path,
      token: this.opts.getToken(),
      ...(extra?.body !== undefined ? { body: extra.body } : {}),
      ...(extra?.form !== undefined ? { form: extra.form } : {}),
      ...(extra?.query !== undefined ? { query: extra.query } : {}),
    });

    if (res.ok) return res.json as T;

    if (res.status === 401) this.opts.onUnauthorized();

    if (isErrorEnvelope(res.json)) {
      const e = res.json.error;
      throw new ApiError({
        code: e.code,
        message: e.message,
        status: res.status,
        requestId: e.requestId ?? null,
        retryAfterSeconds: e.retryAfterSeconds ?? null,
      });
    }
    throw new ApiError({
      code: 'INTERNAL',
      message: 'Unexpected response from the admin API.',
      status: res.status || 500,
    });
  }

  /**
   * A read whose 404 means "nothing here yet", not "something went wrong".
   *
   * Only a route whose empty state the API expresses as a 404 may use this. The brand
   * logo is the one today: before the first upload the API has no asset to describe, so
   * it 404s — and routing that through `request` made the declared `Promise<BrandAsset |
   * null>` a lie. The page then rendered a red error box *instead of* the upload control,
   * on precisely the fresh deployment that needed one. Every other 404 still throws,
   * because everywhere else a missing resource genuinely is the failure.
   */
  private async requestOptional<T>(
    method: HttpMethod,
    path: string,
    extra?: Partial<Pick<RawRequest, 'body' | 'form' | 'query'>>,
  ): Promise<T | null> {
    try {
      return await this.request<T>(method, path, extra);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  }

  // ── auth ──
  /**
   * Sign in. `rememberMe` decides whether the response carries a remember-me lineage as well
   * as an access token; the console never asks for one the operator did not tick.
   */
  login(body: AdminLoginRequest): Promise<AdminLoginResponse> {
    return this.request('POST', ADMIN_ROUTES.login, { body });
  }
  /**
   * Spend a remember-me token for a fresh hour and a fresh token.
   *
   * Single-use and rotating: the token that goes in is burnt, and presenting a spent one kills
   * the whole lineage. That is deliberate reuse detection rather than an inconvenience, and it
   * is why every caller of this must be single-flighted — two concurrent refreshes with the
   * same token look exactly like a stolen token being replayed, and the operator is signed out
   * of every tab as a result. `AuthContext` owns that guard.
   *
   * Unauthenticated, like `login`: the whole point is that the access token has expired.
   *
   * Returns an `AdminSession`, **not** an `AdminLoginResponse`. The difference is one field and
   * it cost the operator their thirty days: a refresh carries the two tokens and no `admin`,
   * because the caller already knows who it is signed in as. This was declared as the login
   * response for months, `AuthContext` believed the type and read `res.admin`, and nothing
   * checked it at runtime — `request` returns `res.json as T`. The console then filed
   * `JSON.stringify(undefined)` under the identity key and could not read its own session back.
   * The declared type is the only guard there is here, so it has to be the true one.
   */
  refreshSession(body: AdminRefreshRequest): Promise<AdminSession> {
    return this.request('POST', ADMIN_ROUTES.refresh, { body });
  }
  /**
   * Burn this browser's remember-me lineage. Idempotent, and best-effort by design: a sign-out
   * clears local storage whether or not this call lands, because a network failure must never
   * leave an operator still signed in on a shared machine.
   *
   * The token has to be named. This route is unauthenticated for the same reason `refresh` is —
   * the caller may hold no access token — so the token in the body *is* the credential, and
   * there is nothing for the server to burn without it. Sent nothing, it answers 400 and the
   * lineage stays live for the rest of its thirty days: a sign-out that only cleared the
   * browser, which on a shared machine is exactly the half that does not matter.
   */
  logoutSession(body: AdminRefreshRequest): Promise<void> {
    return this.request('POST', ADMIN_ROUTES.logout, { body });
  }
  me(): Promise<AdminMe> {
    return this.request('GET', ADMIN_ROUTES.me);
  }

  // ── feature flags ──
  listFlags(): Promise<FlagsResponse> {
    return this.request('GET', ADMIN_ROUTES.flags);
  }
  setFlag(key: string, body: SetFlagRequest): Promise<Flag> {
    return this.request('PUT', ADMIN_ROUTES.flag(key), { body });
  }

  // ── providers / failover ──
  listProviders(): Promise<AdminProvidersResponse> {
    return this.request('GET', ADMIN_ROUTES.providers);
  }
  setProvider(capability: ProviderCapability, body: SetProviderConfigRequest): Promise<ProviderConfig> {
    return this.request('PUT', ADMIN_ROUTES.provider(capability), { body });
  }

  // ── provider credentials / activation ──
  /**
   * Every vendor's card: what it needs, what is configured, and whether it is live —
   * plus which capabilities are still being served by the built-in stub.
   */
  listProviderCredentials(): Promise<ProviderStatusResponse> {
    return this.request('GET', ADMIN_ROUTES.providerCredentials);
  }
  /**
   * The address this deployment calls vendors from, and a live check against one of them.
   *
   * Makes two outbound calls server-side, so it is fetched on demand rather than polled.
   */
  getProviderEgress(): Promise<ProviderEgress> {
    return this.request('GET', ADMIN_ROUTES.providerEgress);
  }
  /**
   * Write one vendor's credentials and/or its switch.
   *
   * `body.values` is a **sparse patch**: a field the operator did not touch must be
   * absent (it keeps whatever is stored) and an explicit `''` clears it. That is not an
   * optimisation — the console cannot render a stored secret back into its input, so a
   * save that sent every field would wipe every secret the operator did not retype.
   *
   * `body.services` is the same idea applied to the per-capability switches, and it is
   * **PROVISIONAL** — see `contracts/pending.ts`. `setProviderCredentialsRequestSchema` is
   * `.strict()`, so an API that has not shipped the field rejects the whole write rather than
   * ignoring it; that is why the editor only ever sends the key when the API has already told
   * it, through `serviceStates`, that it understands one.
   */
  setProviderCredentials(
    slug: string,
    body: SetProviderCredentialsRequest & PendingSetProviderServicesRequest,
  ): Promise<ProviderStatus> {
    return this.request('PUT', ADMIN_ROUTES.providerCredential(slug), { body });
  }

  // ── live rates + operator override ──
  /** Every priced pair: the feed's observation, any standing override, and what quotes use. */
  listRates(): Promise<AdminRatesResponse> {
    return this.request('GET', ADMIN_ROUTES.rates);
  }
  /**
   * Override one pair's price. `body.price` is the literal decimal the operator typed —
   * the API turns it into an exact rational, so the console never does the arithmetic.
   */
  setRateOverride(base: string, quote: string, body: SetRateOverrideRequest): Promise<AdminRateRow> {
    return this.request('PUT', ADMIN_ROUTES.rate(base, quote), { body });
  }
  /** Drop one pair's override, returning it to the live feed. Idempotent. */
  clearRateOverride(base: string, quote: string): Promise<AdminRateRow> {
    return this.request('DELETE', ADMIN_ROUTES.rate(base, quote));
  }
  /** Drop every override at once and get the refreshed board back. */
  resetAllRateOverrides(): Promise<AdminRatesResponse> {
    return this.request('POST', ADMIN_ROUTES.ratesReset);
  }

  // ── deposit rails ──
  /**
   * Every (currency, network) rail with all four figures: what the shipped catalogue says,
   * what the custodian reports, what an operator has overridden, and what is in force.
   *
   * All four rather than only the effective numbers, because "why is this 20 when I typed
   * 12?" is the question this page is asked most and an answer that needs the source is not
   * an answer.
   */
  listAssetRails(): Promise<AdminAssetRailsResponse> {
    return this.request('GET', ADMIN_ROUTES.assetRails);
  }
  /**
   * Override one rail's minimum deposit and/or confirmation depth — or clear one.
   *
   * Three distinct instructions per field, and the console must keep them distinct:
   * **absent** keeps what is stored, **`null`** clears the override so the rail goes back to
   * tracking the catalogue, and a value overrides. Sending today's catalogue figure in place
   * of `null` is not the same thing — it freezes the rail at that number forever.
   */
  setAssetOverride(
    currency: string,
    network: string,
    body: SetAssetOverrideRequest,
  ): Promise<AdminAssetRail> {
    return this.request('PUT', ADMIN_ROUTES.assetRail(currency, network), { body });
  }

  // ── fees ──
  /**
   * Every fee knob with its fallback, the operator's override, and what is in force.
   *
   * All three rather than only the number being charged, because an operator who can see
   * only the effective figure cannot tell an inherited 25 from a typed 25 — and the two
   * behave differently the next time somebody edits the fee being inherited.
   */
  listFees(): Promise<AdminFeesResponse> {
    return this.request('GET', ADMIN_ROUTES.fees);
  }
  /**
   * Set one knob, or clear it.
   *
   * `value: null` **clears** the override, handing the knob back to its environment variable
   * or to the fee it inherits. That is a different instruction from sending today's effective
   * number, which freezes the knob at that figure forever — the console must never quietly
   * turn one into the other.
   */
  setFee(key: string, body: SetFeeRequest): Promise<AdminFee> {
    return this.request('PUT', ADMIN_ROUTES.fee(key), { body });
  }

  // ── users ──
  /**
   * The customer list.
   *
   * Typed with the widened row (`contracts/pending.ts`), which adds the customer's photo and
   * their full phone number as **optional** fields. Optional is the whole point: a server
   * that has not shipped either one simply does not send them, and the pages fall back to the
   * lettermark and to the masked number with a line saying which they are showing.
   */
  listUsers(query?: string, cursor?: string): Promise<AdminUsersResponse> {
    return this.request('GET', ADMIN_ROUTES.users, {
      query: { q: query || undefined, cursor: cursor || undefined },
    });
  }
  getUser(id: string): Promise<AdminUserDetail> {
    return this.request('GET', ADMIN_ROUTES.user(id));
  }
  suspendUser(id: string, body: SuspendUserRequest): Promise<AdminUserDetail> {
    return this.request('POST', ADMIN_ROUTES.suspendUser(id), { body });
  }
  unsuspendUser(id: string): Promise<AdminUserDetail> {
    return this.request('POST', ADMIN_ROUTES.unsuspendUser(id));
  }

  // ── account state ──

  /**
   * Set the account state: `active`, `suspended`, `inactive` or `locked`.
   *
   * One route, and the console still renders three separate controls over it. That is not a
   * contradiction: a lockout, a suspension and a dormancy have different causes and different
   * reversals even though the API stores whichever one is in force in a single column, and
   * three labelled controls that each say what they do beats one dropdown listing five words
   * with no explanation of any of them. The page says out loud that they share a column.
   *
   * `closed` is absent from the settable set on purpose. Closing is the customer's decision.
   *
   * A reason is required on everything except returning to `active`, mirroring the contract's
   * own refine — a restriction with no recorded reason is one nobody can safely reverse six
   * weeks later.
   */
  setAccountState(id: string, body: SetAccountStateRequest): Promise<AdminUserDetail> {
    return this.request('POST', ADMIN_ROUTES.userState(id), { body });
  }

  // ── manual balance movements ──

  /**
   * Credit or debit a customer's balance, with a narration.
   *
   * `amount` is a `MoneyDTO` in integer minor units; the console parses what the operator typed
   * with `BigInt` string surgery and never with `Number`. The response carries the balance
   * *after* the entry committed, so the console never computes one.
   *
   * `idempotencyKey` is required rather than optional, and it is minted once per attempt rather
   * than per request: a double-tapped credit button is the most likely way this endpoint loses
   * money, and it is the one duplicate nobody sees afterwards without reconciling by hand.
   *
   * The customer-facing activity row this writes reads "Top Up" with the platform icon and
   * carries no admin attribution. That is the API's job, in the serializer, inside the same
   * transaction as the posting — not something the console can or should do.
   */
  adjustBalance(id: string, body: AdminAdjustBalanceRequest): Promise<AdminAdjustBalanceResponse> {
    return this.request('POST', ADMIN_ROUTES.userBalance(id), { body });
  }

  /**
   * The customer's profile photo, as bytes.
   *
   * The photo route is authenticated — a customer's face is not a public asset like the brand
   * logo — so a plain `<img src>` cannot load it and this goes through the same binary
   * transport path a support attachment does. `version` is the avatar's `updatedAt`, passed
   * through so a replaced photo bypasses every cache the old one reached while an unchanged
   * one stays cacheable.
   */
  getUserPhoto(id: string, version?: string): Promise<MediaObject> {
    return this.fetchMedia(
      ADMIN_ROUTES.userPhoto(id),
      'profile-photo',
      version === undefined ? undefined : { v: version },
    );
  }

  // ── KYC review ──
  kycQueue(): Promise<AdminKycQueueResponse> {
    return this.request('GET', ADMIN_ROUTES.kycQueue);
  }
  /**
   * One submission.
   *
   * Widened (`contracts/pending.ts`) with the full document number and the document's mime.
   * `adminKycDetailSchema` is `.strict()` and carries neither today, so both arrive absent
   * against the current API and the reviewer sees the last four with a line saying that is
   * all the server sent. Operator screens see the whole value where there is one; where
   * there is not, the console says so rather than presenting four digits as the number.
   */
  kycItem(id: string): Promise<AdminKycDetail> {
    return this.request('GET', ADMIN_ROUTES.kycItem(id));
  }
  /** The documents filed with one submission. Metadata only, never the bytes. */
  kycDocuments(submissionId: string): Promise<KycDocumentsResponse> {
    return this.request('GET', ADMIN_ROUTES.kycDocuments(submissionId));
  }
  /**
   * One document's bytes, addressed by the **document** id and not the submission's.
   *
   * Bearer-scoped and binary, exactly like a support attachment, and for a stronger reason: an
   * identity document is the most sensitive thing this console can render, so it can never be a
   * URL that works without a session. Every fetch writes an audit row naming the one document
   * that was actually looked at, which is the whole argument for it being its own route rather
   * than a field on the queue read.
   */
  getKycDocument(documentId: string): Promise<MediaObject> {
    return this.fetchMedia(ADMIN_ROUTES.kycDocument(documentId), 'identity-document');
  }
  /** Approve or reject a pending KYC submission. A rejection carries a safe-to-show reason. */
  reviewKyc(id: string, body: ReviewKycRequest): Promise<AdminKycDetail> {
    return this.request('POST', ADMIN_ROUTES.kycReview(id), { body });
  }
  /**
   * Raise a customer's tier with no submission behind it.
   *
   * For a customer verified out of band - in person, or through a channel this system never
   * saw. The reason is required and becomes the record, because there are no documents to be
   * one.
   */
  grantTier(userId: string, body: GrantTierRequest): Promise<{ tier: string }> {
    return this.request('POST', ADMIN_ROUTES.userGrantTier(userId), { body });
  }
  /**
   * Re-run the automated vendor chain against a submission already in the manual queue.
   *
   * Failover in the *other* direction: the queue exists because the chain could not decide,
   * and the condition that put it there is usually temporary. On a pass the API raises the
   * tier on the vendor's word, exactly as the customer path would, so the response can come
   * back `verified` and the item leaves the queue — the caller must reload rather than
   * assume the item is still there. `kyc:review` (compliance, superadmin), audited.
   */
  recheckKyc(id: string): Promise<KycRecheckResponse> {
    return this.request('POST', ADMIN_ROUTES.kycRecheck(id));
  }
  /**
   * The queue's watchdog: how many submissions are waiting and how old the oldest is.
   *
   * Its own cheap read rather than a field on the queue response, so a dashboard can poll
   * one number without paging the queue. `stale` and `staleAfterSeconds` are computed by
   * the API from the contract's `KYC_MANUAL_REVIEW_SLA_SECONDS`, so the console renders the
   * API's verdict instead of carrying its own copy of the threshold and disagreeing.
   */
  kycPending(): Promise<AdminKycPendingResponse> {
    return this.request('GET', ADMIN_ROUTES.kycPending);
  }

  // ── KYC tier ceilings ──
  /** All four tiers' single/daily/monthly ceilings, with who last moved each and when. */
  listTierLimits(): Promise<AdminTierLimitsResponse> {
    return this.request('GET', ADMIN_ROUTES.limitTiers);
  }
  /**
   * Write one tier's ceilings. Amounts are naira in kobo as integer strings — the console
   * converts what the operator typed with `BigInt`, never a float.
   *
   * The API refuses a non-NGN ceiling, a negative one, and any set that breaks
   * `single ≤ daily ≤ monthly`. The page checks the ordering too, but only to spare a
   * round-trip: this route is the authority and the numbers take effect on the next limit
   * check, which is to say immediately.
   */
  setTierLimits(tier: string, body: SetTierLimitsRequest): Promise<TierLimit> {
    return this.request('PUT', ADMIN_ROUTES.limitTier(tier), { body });
  }

  // ── transactions + reconciliation ──
  /**
   * The money-event feed.
   *
   * Widened with the customer's full phone and with `providerCost` — what the *vendor*
   * charged us on this movement, as opposed to what the platform charged the customer. Both
   * optional, both rendered as a stated absence when the API does not send them, because a
   * blank "Provider fee" cell would read as "no fee" rather than as "not reported".
   */
  listTransactions(filters?: TransactionFilters): Promise<AdminTxResponse> {
    return this.request('GET', ADMIN_ROUTES.transactions, {
      query: {
        kind: filters?.kind,
        status: filters?.status,
        from: filters?.from,
        to: filters?.to,
        cursor: filters?.cursor,
        limit: filters?.limit === undefined ? undefined : String(filters.limit),
      },
    });
  }
  /**
   * One movement in full, for the panel that opens when a row is clicked.
   *
   * A second call rather than more columns on the list, because it is four more queries per
   * row and a list is fifty rows. Fetched only when a panel opens, so the rows nobody opens
   * cost nothing.
   */
  transaction(id: string): Promise<AdminTxDetail> {
    return this.request('GET', ADMIN_ROUTES.transaction(id));
  }

  reconciliation(): Promise<ReconciliationResponse> {
    return this.request('GET', ADMIN_ROUTES.reconciliation);
  }
  runReconciliation(): Promise<ReconciliationResponse> {
    return this.request('POST', ADMIN_ROUTES.runReconciliation);
  }

  // ── brand assets ──
  /**
   * The current operator logo's metadata, or null before any upload.
   *
   * The API answers 404 until a logo exists, which is an empty state and not an error —
   * hence `requestOptional`, so the declared `| null` is the truth rather than a comment.
   */
  brandLogo(): Promise<BrandAsset | null> {
    return this.requestOptional('GET', ADMIN_ROUTES.brandLogo);
  }
  /** Replace the operator logo. The bytes travel as base64 in the body (PUT), per contract. */
  uploadBrandLogo(body: UploadBrandAssetRequest): Promise<BrandAsset> {
    return this.request('PUT', ADMIN_ROUTES.brandLogo, { body });
  }
  /**
   * The brand icon — the square mark the app uses for the favicon, the avatar on a
   * GenEx-to-GenEx transfer, and the notification icon.
   *
   * `requestOptional` for the reason the logo read uses it: 404 until one exists is an empty
   * state, not a failure, and routing it through `request` would put a red error box where the
   * upload control belongs on precisely the fresh deployment that needs the upload control.
   */
  brandIcon(): Promise<BrandAsset | null> {
    return this.requestOptional('GET', ADMIN_ROUTES.brandIcon);
  }
  uploadBrandIcon(body: UploadBrandAssetRequest): Promise<BrandAsset> {
    return this.request('PUT', ADMIN_ROUTES.brandIcon, { body });
  }
  /** One balance header's artwork, or null when that surface has none. */
  brandHero(surface: BrandHeroSurface): Promise<BrandHeroAsset | null> {
    return this.requestOptional('GET', ADMIN_ROUTES.brandHero(surface));
  }
  uploadBrandHero(
    surface: BrandHeroSurface,
    body: UploadBrandAssetRequest,
  ): Promise<BrandHeroAsset> {
    return this.request('PUT', ADMIN_ROUTES.brandHero(surface), { body });
  }
  /**
   * Choose how text over one header is drawn.
   *
   * One word, so no raised body limit and no image round trip: the colour is adjusted while
   * looking at the result, and re-uploading a photograph to change a word would be absurd.
   */
  setBrandHeroTextColor(
    surface: BrandHeroSurface,
    body: SetHeroTextColorRequest,
  ): Promise<BrandHeroAsset> {
    return this.request('PUT', ADMIN_ROUTES.brandHeroText(surface), { body });
  }
  /** Clear one header's artwork, returning it to a plain background. */
  clearBrandHero(surface: BrandHeroSurface): Promise<void> {
    return this.request('DELETE', ADMIN_ROUTES.brandHero(surface));
  }
  /**
   * The current splash asset's metadata — mime, decoded size, and the dimensions the API
   * read out of the file header — or null before any upload.
   *
   * `requestOptional` for exactly the reason the logo read uses it: the API answers 404
   * until a splash exists, and that is an empty state rather than a failure. Routing it
   * through `request` would put a red error box on the page in place of the upload control,
   * on precisely the fresh deployment where the upload control is the only thing wanted.
   */
  brandSplash(): Promise<SplashAsset | null> {
    return this.requestOptional('GET', ADMIN_ROUTES.brandSplash);
  }
  /**
   * Replace the splash. Bytes as base64, same as the logo, but a different route because the
   * rules differ: it accepts `video/mp4` as well as the raster mimes, enforces a portrait
   * requirement on a still, and carries a ceiling six times larger. The API sniffs the bytes
   * against the declared mime and reads the dimensions from the file header, so what the
   * console checks before sending is a courtesy and the server is the authority.
   */
  uploadBrandSplash(body: UploadSplashAssetRequest): Promise<SplashAsset> {
    return this.request('PUT', ADMIN_ROUTES.brandSplash, { body });
  }

  // ── bank directory ──
  /** The whole directory the worker last synced, active and inactive alike. */
  listBanks(): Promise<AdminBanksResponse> {
    return this.request('GET', ADMIN_ROUTES.banks);
  }
  /**
   * Replace one bank's logo. Same body as the operator logo — one upload shape across the
   * console — addressed by the bank's opaque public id.
   */
  uploadBankLogo(bankId: string, body: UploadBrandAssetRequest): Promise<AdminBank> {
    return this.request('PUT', ADMIN_ROUTES.bankLogo(bankId), { body });
  }

  /**
   * A gift-card brand's icon.
   *
   * Until this existed the icon could only be planted by the seed, so a brand added from the
   * console drew a lettermark for ever with nothing anywhere to fix it. The app has always
   * rendered these: beside every brand in the catalogue, and on every past redemption in a
   * customer's activity feed.
   */
  uploadGiftcardBrandLogo(
    brandId: string,
    body: UploadBrandAssetRequest,
  ): Promise<AdminGiftcardBrand> {
    return this.request('PUT', ADMIN_ROUTES.giftcardBrandLogo(brandId), { body });
  }

  // ── home-screen banners ──
  /**
   * Every banner, running or not, in the order the app would show them.
   *
   * Typed with the themed row (see `contracts/banner-theme.ts`): the colour and destination
   * fields are optional, so this is correct against an API that returns them and against one
   * that does not.
   */
  listBanners(): Promise<AdminBannersResponse> {
    return this.request('GET', ADMIN_ROUTES.banners);
  }
  /**
   * Publish a banner.
   *
   * `deepLink` is a key from the contract's closed list, never a URL — the API refuses
   * anything else, so the page offers a picker rather than a text field. `externalUrl` is the
   * separate, deliberate exception: an operator-typed web address, accepted only as `https`,
   * which the app opens in a browser rather than resolving to a route. The closed list is what
   * protects the *router*; the https rule is what protects everything else.
   */
  createBanner(body: CreateBannerRequest): Promise<AdminBanner> {
    return this.request('POST', ADMIN_ROUTES.banners, { body });
  }
  /** Patch one banner. Absent fields are left alone, so a reorder sends only `sortOrder`. */
  updateBanner(id: string, body: UpdateBannerRequest): Promise<AdminBanner> {
    return this.request('PATCH', ADMIN_ROUTES.banner(id), { body });
  }
  /** Remove a banner outright. The API keeps the audit entry, not the row. */
  deleteBanner(id: string): Promise<void> {
    return this.request('DELETE', ADMIN_ROUTES.banner(id));
  }
  /** Replace a banner's artwork. Same upload body as the operator logo and bank marks. */
  uploadBannerImage(id: string, body: UploadBrandAssetRequest): Promise<AdminBanner> {
    return this.request('PUT', ADMIN_ROUTES.bannerImage(id), { body });
  }

  // ── gift-card redemption review ──
  giftcardQueue(): Promise<AdminRedemptionQueueResponse> {
    return this.request('GET', ADMIN_ROUTES.giftcardQueue);
  }
  /** The reviewer-only detail: the full card code and image, served only to an admin. */
  giftcardItem(id: string): Promise<AdminRedemptionDetail> {
    return this.request('GET', ADMIN_ROUTES.giftcardItem(id));
  }
  /** Approve (credits the customer's naira wallet) or reject (with a reason) a redemption. */
  reviewGiftcard(id: string, body: ReviewRedemptionRequest): Promise<AdminRedemptionDetail> {
    return this.request('POST', ADMIN_ROUTES.giftcardReview(id), { body });
  }
  // ── gift-card brand catalogue ──
  /**
   * Every brand, active or not, in display order — the operator shape, which is the
   * customer's plus `sortOrder` and `createdAt`.
   *
   * Not filtered on `active`, unlike the customer catalogue, because this is where a brand
   * gets switched back on and a list that hid switched-off brands would make that
   * impossible. Gated on the finance roles (`rates:write`) like the writes below rather
   * than on any admin: a rate is the desk's margin, and there is no support question that
   * needs it. So a role without that grant is refused on this *read*, and a page that only
   * disabled its buttons would still error-box on load.
   */
  listGiftcardBrandsAdmin(): Promise<AdminGiftcardBrandsResponse> {
    return this.request('GET', ADMIN_ROUTES.giftcardBrands);
  }
  /**
   * Create a brand. `rateNairaPerUnit` is the literal decimal the operator typed — naira
   * per one whole unit of face value — and the API turns it into the exact rational it
   * stores, so the console never does that arithmetic on the value it sends.
   *
   * No rate-deviation guard applies here, deliberately: a first rate has nothing to
   * deviate from. That is also the deliberate escape hatch for a genuine ten-fold
   * repricing, which the update path refuses in one write.
   */
  createGiftcardBrand(body: CreateGiftcardBrandRequest): Promise<AdminGiftcardBrand> {
    return this.request('POST', ADMIN_ROUTES.giftcardBrands, { body });
  }
  /**
   * Patch one brand. Every field is optional and an absent one is left alone, so flipping
   * `active` sends `active` and nothing else — which matters, because a full-object PUT
   * would re-send the rate and trip the deviation guard on an edit that never touched it.
   */
  updateGiftcardBrand(id: string, body: UpdateGiftcardBrandRequest): Promise<AdminGiftcardBrand> {
    return this.request('PATCH', ADMIN_ROUTES.giftcardBrand(id), { body });
  }

  // ── support console ──
  /**
   * The operator thread inbox, filterable by status/priority/assignee.
   *
   * The rows carry a full phone number wherever the API supplies one. This used to be
   * described as the "masked-customer" inbox; that is no longer what it is, and a comment
   * describing behaviour the code does not have is worse than none.
   */
  listInbox(filters?: SupportInboxFilters): Promise<AdminInboxResponse> {
    return this.request('GET', ADMIN_ROUTES.supportInbox, {
      query: {
        status: filters?.status,
        priority: filters?.priority,
        assignedAdminId: filters?.assignedAdminId,
        cursor: filters?.cursor,
      },
    });
  }
  /** One thread's full detail: the row plus a page of its messages. */
  getThread(id: string): Promise<AdminThreadDetail> {
    return this.request('GET', ADMIN_ROUTES.supportThread(id));
  }
  /**
   * Post an operator message (optionally moving status in the same action) and get the
   * refreshed thread back, so the composer can replace state in one round-trip.
   */
  reply(id: string, body: AdminReplyRequest): Promise<AdminThreadDetail> {
    return this.request('POST', ADMIN_ROUTES.supportReply(id), { body });
  }
  /** Assign/reassign a thread (or unassign with null) and optionally set priority. */
  assign(id: string, body: AssignThreadRequest): Promise<AdminThreadRow> {
    return this.request('POST', ADMIN_ROUTES.supportAssign(id), { body });
  }
  /** Read the support configuration (email, auto-reply, business hours). */
  getSettings(): Promise<SupportSettings> {
    return this.request('GET', ADMIN_ROUTES.supportSettings);
  }
  /** Patch the support configuration. Superadmin only (the API enforces). */
  setSettings(body: SetSupportSettingsRequest): Promise<SupportSettings> {
    return this.request('PUT', ADMIN_ROUTES.supportSettings, { body });
  }
  /**
   * Fetch one attachment's bytes over the bearer-scoped media route and return an object URL.
   * Goes through the transport's binary path (`sendMedia`) so image/PDF bytes are never
   * `JSON.parse`d. The caller must `revoke()` the URL once the attachment is no longer shown.
   */
  getMediaObject(id: string): Promise<MediaObject> {
    return this.fetchMedia(ADMIN_ROUTES.supportMedia(id), 'attachment');
  }

  /**
   * The one binary read path, shared by everything in this console that renders bytes behind
   * a bearer token: a support attachment, a customer's profile photo, an identity document,
   * a bill-service icon.
   *
   * It exists as one function rather than four because the failure modes are identical and
   * subtle. The bytes must never touch `JSON.parse` — that is the Wave-1 bug where a chat
   * image blanked because its payload was parsed as JSON. A 401 must drop the session exactly
   * as the JSON path does, or an expired token leaves a page half-usable. And the object URL
   * must be handed back with its own `revoke`, because a component that forgets to release
   * one leaks the whole image for the lifetime of the tab.
   *
   * `mime` prefers what the blob reports and falls back to `application/octet-stream` rather
   * than guessing `image/*`: a viewer that assumes an image and is handed a PDF renders a
   * broken frame, which is the one thing an identity-document viewer must never do.
   */
  private async fetchMedia(
    path: string,
    fallbackName: string,
    query?: Readonly<Record<string, string>>,
  ): Promise<MediaObject> {
    const res = await this.opts.transport.sendMedia({
      method: 'GET',
      path,
      token: this.opts.getToken(),
      ...(query === undefined ? {} : { query }),
    });
    if (res.ok && res.blob !== null) {
      const url = URL.createObjectURL(res.blob);
      const mime = res.blob.type || 'application/octet-stream';
      const name = res.name ?? fallbackName;
      return { url, mime, name, revoke: () => URL.revokeObjectURL(url) };
    }
    if (res.status === 401) this.opts.onUnauthorized();
    if (isErrorEnvelope(res.json)) {
      const e = res.json.error;
      throw new ApiError({
        code: e.code,
        message: e.message,
        status: res.status,
        requestId: e.requestId ?? null,
        retryAfterSeconds: e.retryAfterSeconds ?? null,
      });
    }
    throw new ApiError({
      code: 'INTERNAL',
      message: 'Those bytes could not be loaded.',
      status: res.status || 500,
    });
  }
  /**
   * The API-relative media path (with the `/v1` prefix). Not directly loadable in an
   * `<img src>` because the route is bearer-scoped — the UI renders bytes via `getMediaObject`.
   * Exposed for logging, deep-linking, and download-name labelling.
   */
  mediaUrl(id: string): string {
    return `${API_PREFIX}${ADMIN_ROUTES.supportMedia(id)}`;
  }

  // ── Business Settings (white-label config hub) ──
  /** Read the single platform-settings row (name, logo, colours, contacts, links, socials). */
  getBusinessSettings(): Promise<PlatformSettings> {
    return this.request('GET', ADMIN_ROUTES.settingsBusiness);
  }
  /** Patch the platform settings. Superadmin only (the API enforces). */
  setBusinessSettings(body: SetPlatformSettingsRequest): Promise<PlatformSettings> {
    return this.request('PUT', ADMIN_ROUTES.settingsBusiness, { body });
  }

  // ── Legal & about documents ──
  /**
   * All three documents (terms, privacy, about) as stored — bodies with their `{{tokens}}`
   * intact, because the console edits the source and the API substitutes on the public read.
   */
  listLegalDocuments(): Promise<AdminLegalDocumentsResponse> {
    return this.request('GET', ADMIN_ROUTES.legalDocuments);
  }
  /** Publish one document by key. Superadmin only, audited (the API enforces both). */
  publishLegalDocument(key: string, body: UpsertLegalDocumentRequest): Promise<AdminLegalDocument> {
    return this.request('PUT', ADMIN_ROUTES.legalDocument(key), { body });
  }

  // ── Email templates ──
  /** Every editable transactional template. */
  listEmailTemplates(): Promise<EmailTemplatesResponse> {
    return this.request('GET', ADMIN_ROUTES.emailTemplates);
  }
  /** Upsert one template by key (name/subject/HTML/vars/enabled). */
  upsertEmailTemplate(key: string, body: UpsertEmailTemplateRequest): Promise<EmailTemplate> {
    return this.request('PUT', ADMIN_ROUTES.emailTemplate(key), { body });
  }
  /** Render a template against optional sample vars, without sending — the live preview. */
  previewEmailTemplate(key: string, body: RenderPreviewRequest): Promise<RenderPreviewResponse> {
    return this.request('POST', ADMIN_ROUTES.emailTemplatePreview(key), { body });
  }
  /** Send a live test of a template to one address. */
  testSendEmailTemplate(key: string, body: TestSendRequest): Promise<TestSendResult> {
    return this.request('POST', ADMIN_ROUTES.emailTemplateTestSend(key), { body });
  }

  /**
   * Put one template back to the shipped default.
   *
   * Snapshots the current content into the version history first, so this is recoverable
   * rather than destructive: an operator who restores by mistake has not lost their work.
   */
  restoreEmailTemplate(key: string): Promise<EmailTemplate> {
    return this.request('POST', ADMIN_ROUTES.emailTemplateRestore(key));
  }

  // ── The email layout: the branded chrome every message is poured into ──

  /**
   * The active layout.
   *
   * `requestOptional` because a deployment whose layout row has never been written answers
   * 404, and that is an empty state the editor fills from the contract's documented default,
   * not a failure worth a red box over a working page.
   */
  getEmailLayout(): Promise<EmailLayout | null> {
    return this.requestOptional('GET', ADMIN_ROUTES.emailLayout);
  }
  /**
   * Save a new layout version.
   *
   * The whole content travels; there is no partial patch of a version. Append-only server
   * side, so this inserts a version and moves the active marker rather than overwriting, and
   * a palette an operator regrets is one restore away rather than gone.
   */
  updateEmailLayout(body: UpdateEmailLayoutRequest): Promise<EmailLayout> {
    return this.request('PUT', ADMIN_ROUTES.emailLayout, { body });
  }
  /** The version history, newest first. Enough to choose one to restore, without the bodies. */
  emailLayoutVersions(): Promise<EmailLayoutHistoryResponse> {
    return this.request('GET', ADMIN_ROUTES.emailLayoutVersions);
  }
  /** Copy an earlier version forward as a new one. */
  restoreEmailLayout(body: RestoreEmailLayoutRequest): Promise<EmailLayout> {
    return this.request('POST', ADMIN_ROUTES.emailLayoutRestore, { body });
  }
  /** Render a sample message through the layout, so the chrome can be judged on its own. */
  previewEmailLayout(body: RenderPreviewRequest): Promise<RenderPreviewResponse> {
    return this.request('POST', ADMIN_ROUTES.emailLayoutPreview, { body });
  }

  // ── Images an admin may place in mail ──

  /** Every uploaded image. Small and unpaged by contract (200 max). */
  listEmailImages(): Promise<EmailImagesResponse> {
    return this.request('GET', ADMIN_ROUTES.emailImages);
  }
  /**
   * Upload one image.
   *
   * Base64 rather than multipart, matching every other asset upload in this console. The
   * content type is deliberately NOT sent: the server sniffs the real bytes, because a
   * content-type header is a claim and a magic number is evidence.
   */
  uploadEmailImage(body: UploadEmailImageRequest): Promise<EmailImage> {
    return this.request('POST', ADMIN_ROUTES.emailImages, { body });
  }
  /** Delete one image. Anything already sent that references it keeps pointing at nothing. */
  deleteEmailImage(id: string): Promise<void> {
    return this.request('DELETE', ADMIN_ROUTES.emailImage(id));
  }

  // ── Broadcasts ──

  listEmailBroadcasts(): Promise<EmailBroadcastsResponse> {
    return this.request('GET', ADMIN_ROUTES.emailBroadcasts);
  }
  /**
   * How many a selector would reach, before committing to sending it.
   *
   * Counts only, by contract: returning the matching customers would turn the composer into a
   * customer-search endpoint under a different name. `deliverable` is the number that matters,
   * and it is smaller than `matched` by everyone with no address or an opt-out.
   */
  previewEmailBroadcast(body: BroadcastPreviewRequest): Promise<BroadcastPreviewResponse> {
    return this.request('POST', ADMIN_ROUTES.emailBroadcastsPreview, { body });
  }
  /**
   * Create a draft. **This does not send anything**, and the split is the point: composing and
   * sending are two decisions, and only the second one is irreversible.
   */
  createEmailBroadcast(body: CreateBroadcastRequest): Promise<EmailBroadcastDetail> {
    return this.request('POST', ADMIN_ROUTES.emailBroadcasts, { body });
  }
  getEmailBroadcast(id: string): Promise<EmailBroadcastDetail> {
    return this.request('GET', ADMIN_ROUTES.emailBroadcast(id));
  }
  /**
   * Send a draft. Irreversible, outward-facing, audited.
   *
   * The audience resolves here rather than at creation, so the population is fixed at the
   * moment of sending rather than at the moment of composing.
   */
  sendEmailBroadcast(id: string): Promise<EmailBroadcastDetail> {
    return this.request('POST', ADMIN_ROUTES.emailBroadcastSend(id));
  }
  /** Stop one. Recipients already sent to stay sent; there is no recall. */
  cancelEmailBroadcast(id: string): Promise<EmailBroadcast> {
    return this.request('POST', ADMIN_ROUTES.emailBroadcastCancel(id));
  }

  // ── Prove the mail server works ──

  /**
   * Open a real submission session against these settings and send one real message.
   *
   * Nothing is saved: the point is that a configuration which cannot deliver never becomes the
   * live one. `password` is optional and the console omits it unless the operator typed a new
   * one, because a stored secret is never returned and a test that demanded one would force a
   * retype or a save-then-test, which is the order this exists to prevent.
   *
   * A `delivered: false` verdict is a 200. It is an answer, not a transport failure, and
   * mapping it to a thrown error would lose the vendor's own rejection text, which is usually
   * the most useful sentence available to whoever now has to fix something.
   */
  smtpTest(body: SmtpTestRequest): Promise<SmtpTestResponse> {
    return this.request('POST', ADMIN_ROUTES.smtpTest, { body });
  }

  // ── Bill services ──

  /**
   * Every bill service, available or not.
   *
   * Unfiltered, unlike the customer catalogue, because this is where a biller gets switched
   * back on and a list that hid unavailable services would make that impossible. The same
   * argument the gift-card brand catalogue makes.
   */
  listBillServices(): Promise<{ readonly services: readonly BillService[] }> {
    return this.request('GET', ADMIN_ROUTES.billServices);
  }
  /**
   * Patch the mark, the colour, or availability. Absent fields are left alone.
   *
   * **PROVISIONAL** — see `contracts/pending.ts`. The contract shipped the list and the icon
   * upload but no per-service write, so the letter mark and the hex colour behind it are still
   * seed-only. They matter precisely because they are the fallback every service without an
   * uploaded icon is drawn from, which today is all of them.
   */
  updateBillService(id: string, body: PendingUpdateBillServiceRequest): Promise<BillService> {
    return this.request('PATCH', PENDING_ROUTES.billService(id), { body });
  }

  /**
   * **DISPOSABLE.** Run the Busha sandbox probe. Goes with `pages/BushaProbePage.tsx`.
   *
   * Returns `unknown` on purpose: there is no contract behind this route and there is not
   * going to be one, so the page reads the body through `readBushaProbe` rather than asserting
   * a shape neither side has agreed. The key travels in the body and nowhere else.
   */
  bushaProbe(body: PendingBushaProbeRequest): Promise<unknown> {
    return this.request('POST', PENDING_ROUTES.bushaProbe, { body });
  }
  /**
   * Replace one service's icon. Base64 in the body, matching every other asset upload here.
   *
   * Raster only, under 200 KB. An SVG is an active document that executes when it is displayed
   * and these bytes are served from a public route to every customer's phone; the console
   * refuses one before it is read and the API sniffs the magic number and refuses it again.
   */
  uploadBillServiceIcon(id: string, body: UploadBillIconRequest): Promise<BillService> {
    return this.request('PUT', ADMIN_ROUTES.billServiceIcon(id), { body });
  }
  /**
   * The stored icon's bytes, for the operator's preview.
   *
   * Read through the admin route rather than composed into a public `<img src>`, even though
   * the serving path *is* public. Composing a public URL in the console means guessing at an
   * origin, and a console running on its mock transport has no origin to guess at: it would
   * render a broken frame exactly where a preview belongs. The public path is printed as text
   * beside the tile instead, so an operator can still recognise where the file will be served.
   */
  getBillServiceIcon(id: string, version?: string): Promise<MediaObject> {
    return this.fetchMedia(
      ADMIN_ROUTES.billServiceIcon(id),
      'service-icon',
      version === undefined ? undefined : { v: version },
    );
  }

  // ── Push (operator broadcast) ──
  /** Fan a push message out to the customer base by category. Superadmin only, audited. */
  broadcastPush(body: BroadcastPushRequest): Promise<PushBroadcastResult> {
    return this.request('POST', ADMIN_ROUTES.pushBroadcast, { body });
  }

  // ── Treasury ──
  /**
   * The desk's dashboard: what the custodian holds, what customers are owed, and the float.
   *
   * Read fresh on every open rather than cached anywhere in the console. The operator's whole
   * reason for looking is to decide whether there is enough money to keep converting, and a
   * cached answer to that question is worse than a slow one.
   */
  treasuryOverview(): Promise<TreasuryOverviewResponse> {
    return this.request('GET', ADMIN_ROUTES.treasuryOverview);
  }

  /** Every order the desk can see, newest first. Quotes nobody accepted are excluded. */
  listTreasuryOrders(): Promise<TreasuryReviewQueueResponse> {
    return this.request('GET', ADMIN_ROUTES.treasuryOrders);
  }

  /** One order with every leg, every vendor reference and every raw payload. */
  getTreasuryOrder(id: string): Promise<AdminTreasuryOrder> {
    return this.request('GET', ADMIN_ROUTES.treasuryOrder(id));
  }

  /** Everything waiting on a human. */
  treasuryReviewQueue(): Promise<TreasuryReviewQueueResponse> {
    return this.request('GET', ADMIN_ROUTES.treasuryReview);
  }

  /**
   * Decide a held order. Finance only.
   *
   * `approve` clears the hold and lets the worker resume; `reprice` applies the amount that
   * actually arrived even though it fell outside the automatic tolerance; `refund` and
   * `reject` send it back to the customer. Only `reprice` changes what they are paid.
   */
  decideTreasuryReview(id: string, body: ReviewOrderRequest): Promise<AdminTreasuryOrder> {
    return this.request('POST', ADMIN_ROUTES.treasuryReviewOrder(id), { body });
  }

  /** Moves the desk already made. */
  listTreasuryRebalances(): Promise<{ rebalances: TreasuryRebalance[] }> {
    return this.request('GET', ADMIN_ROUTES.treasuryRebalances);
  }

  /**
   * Record a move the desk already made. Recording, never executing.
   *
   * Nothing here places an order anywhere: an operator bought coin from a counterparty by
   * whatever means they normally use, and this is how the books learn the rate they got.
   */
  recordTreasuryRebalance(body: RecordRebalanceRequest): Promise<TreasuryRebalance> {
    return this.request('POST', ADMIN_ROUTES.treasuryRebalances, { body });
  }

  treasuryThresholds(): Promise<TreasuryThresholds> {
    return this.request('GET', ADMIN_ROUTES.treasuryThresholds);
  }

  /** The liquidity floors, the review ceiling and the pause switch. Finance only. */
  setTreasuryThresholds(body: SetTreasuryThresholdsRequest): Promise<TreasuryThresholds> {
    return this.request('PUT', ADMIN_ROUTES.treasuryThresholds, { body });
  }

  treasuryReconciliation(): Promise<TreasuryReconciliationResponse> {
    return this.request('GET', ADMIN_ROUTES.treasuryReconciliation);
  }

  /**
   * Ask for a run. Resolves when the job is *queued*, not when it finishes.
   *
   * The API answers 202 for that reason — a full reconciliation walks every custodian balance
   * and every unmatched movement, far past the point where an HTTP request should still be
   * open. The page says "queued" rather than pretending to a result it does not have.
   */
  runTreasuryReconciliation(scope: ReconciliationScope): Promise<{ scope: string; status: string }> {
    return this.request('POST', ADMIN_ROUTES.treasuryReconciliationRun, { body: { scope } });
  }
}
